import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { motion, useScroll, useSpring, useMotionValueEvent, useReducedMotion } from "framer-motion"

const EASE = [0.22, 1, 0.36, 1]
const GOLD = "#C9A24D"

const chapters = [
  {
    period: "The Spark",
    title: "Turning Passion Into Profession",
    text: "I consider myself fortunate to be among the few who can truly say “Do what you love every single day.” Success comes from knowledge, experience, and building a motivated team.",
    img: "/assets/about/sec1.jpg",
  },
  {
    period: "2008 – 2010",
    title: "Ferrari & Aston Martin",
    text: "Working with Ferrari dealerships in the UK and Aston Martin in Mumbai refined my understanding of luxury, precision, and leadership.",
    img: "/assets/about/sec2.jpg",
  },
  {
    period: "2011",
    title: "Learning On The Ground",
    text: "Working closely with independent garage owners in Pune and Mumbai shaped my belief in India’s automotive potential.",
    img: "/assets/about/sec3.jpg",
  },
  {
    period: "2014 – 2015",
    title: "Foundation & First Steps",
    text: "This phase marked the beginning of a new chapter built on honesty, quality, and relentless passion.",
    img: "/assets/about/sec4.jpg",
  },
]

/* ------------------------------------------------------------------ */
/*  The winding road (SVG, measured from the real layout)              */
/* ------------------------------------------------------------------ */
function Road({ size, stops, desktop, progress, reached, onStops }) {
  const pathRef = useRef(null)
  const fillRef = useRef(null)
  const carRef = useRef(null)
  const lenRef = useRef(0)

  const { w, h } = size
  const cx = desktop ? w / 2 : 26
  const amp = desktop ? 34 : 7
  const roadW = desktop ? 40 : 28

  // Build a smooth S-curve road through every milestone
  const pts = [{ x: cx, y: 0 }]
  stops.forEach((y, i) => pts.push({ x: cx + (i % 2 === 0 ? -amp : amp), y }))
  pts.push({ x: cx, y: h })

  let d = `M ${pts[0].x} ${pts[0].y}`
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1]
    const b = pts[i]
    const dy = b.y - a.y
    d += ` C ${a.x} ${a.y + dy * 0.5}, ${b.x} ${b.y - dy * 0.5}, ${b.x} ${b.y}`
  }

  const place = (p) => {
    const path = pathRef.current
    const len = lenRef.current
    if (!path || !len) return
    const c = Math.max(0, Math.min(1, p))
    const at = c * len
    const a = path.getPointAtLength(at)
    const b = path.getPointAtLength(Math.min(len, at + 1))
    const deg = (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI
    if (carRef.current) carRef.current.setAttribute("transform", `translate(${a.x} ${a.y}) rotate(${deg})`)
    if (fillRef.current) fillRef.current.style.strokeDashoffset = String(len * (1 - c))
  }

  // re-measure whenever the road's shape changes
  useLayoutEffect(() => {
    const path = pathRef.current
    if (!path) return
    const len = path.getTotalLength()
    lenRef.current = len
    if (fillRef.current) fillRef.current.style.strokeDasharray = String(len)
    place(progress.get())

    // at which point along the road (0..1) does the car reach each stop?
    const fracs = stops.map((y) => {
      let lo = 0
      let hi = len
      for (let i = 0; i < 24; i++) {
        const mid = (lo + hi) / 2
        if (path.getPointAtLength(mid).y < y) lo = mid
        else hi = mid
      }
      return len ? lo / len : 0
    })
    onStops(fracs)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d])

  useMotionValueEvent(progress, "change", place)

  if (!w || !h) return null

  return (
    <svg
      className="pointer-events-none absolute inset-0 overflow-visible"
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      aria-hidden
    >
      <defs>
        <filter id="roadGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* shoulder / kerb */}
      <path d={d} fill="none" stroke="#3a3a3a" strokeWidth={roadW + 4} strokeLinecap="round" />
      {/* asphalt */}
      <path ref={pathRef} d={d} fill="none" stroke="#1b1b1b" strokeWidth={roadW} strokeLinecap="round" />
      {/* dashed lane markings */}
      <path d={d} fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="2" strokeDasharray="12 16" />
      {/* travelled stretch – glows gold behind the car */}
      <path
        ref={fillRef}
        d={d}
        fill="none"
        stroke={GOLD}
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray="1"
        strokeDashoffset="1"
        filter="url(#roadGlow)"
      />

      {/* milestone stops – light up when the car arrives */}
      {stops.map((y, i) => {
        const x = cx + (i % 2 === 0 ? -amp : amp)
        const r = desktop ? 15 : 12
        const on = i < reached
        return (
          <g key={i} transform={`translate(${x} ${y})`}>
            {on && (
              <motion.circle
                initial={{ r, opacity: 0.9 }}
                animate={{ r: r + 34, opacity: 0 }}
                transition={{ duration: 1.1, ease: "easeOut" }}
                fill="none"
                stroke={GOLD}
                strokeWidth="2"
              />
            )}
            <circle r={r + 5} fill={GOLD} opacity={on ? 0.25 : 0.08} />
            <circle r={r} fill={on ? GOLD : "#0f0f0f"} stroke={GOLD} strokeWidth="2" style={{ transition: "fill 0.4s" }} />
            <text
              textAnchor="middle"
              dominantBaseline="central"
              fill={on ? "#0f0f0f" : GOLD}
              fontSize={desktop ? 12 : 10}
              fontWeight="700"
            >
              {i + 1}
            </text>
          </g>
        )
      })}

      {/* the car (top-down, nose points along +x) */}
      <g ref={carRef}>
        <g filter="url(#roadGlow)">
          <rect x="-15" y="-8" width="30" height="16" rx="5" fill="#dc2626" />
          <rect x="-4" y="-6" width="10" height="12" rx="2" fill="#111" opacity="0.85" />
          <rect x="7" y="-6" width="5" height="12" rx="1.5" fill="#111" opacity="0.6" />
          <circle cx="14" cy="-5" r="1.8" fill="#fde68a" />
          <circle cx="14" cy="5" r="1.8" fill="#fde68a" />
          <circle cx="-14" cy="-5" r="1.4" fill="#7f1d1d" />
          <circle cx="-14" cy="5" r="1.4" fill="#7f1d1d" />
        </g>
      </g>
    </svg>
  )
}

/* ------------------------------------------------------------------ */
/*  One chapter row – stays hidden until the car arrives               */
/* ------------------------------------------------------------------ */
function Corner({ className, active, delay }) {
  return (
    <motion.span
      initial={{ opacity: 0, scale: 0.3 }}
      animate={active ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.3 }}
      transition={{ type: "spring", stiffness: 220, damping: 18, delay }}
      className={`absolute h-6 w-6 border-[#C9A24D] md:h-8 md:w-8 ${className}`}
    />
  )
}

function MaskedWords({ text, active, delay = 0 }) {
  return (
    <span aria-label={text}>
      {text.split(" ").map((word, i) => (
        <span key={i} className="inline-block overflow-hidden pr-[0.25em] align-bottom" aria-hidden>
          <motion.span
            className="inline-block"
            initial={{ y: "115%" }}
            animate={{ y: active ? "0%" : "115%" }}
            transition={{ duration: 0.9, ease: EASE, delay: delay + i * 0.07 }}
          >
            {word}
          </motion.span>
        </span>
      ))}
    </span>
  )
}

function Chapter({ data, index, rowRef, active }) {
  const flip = index % 2 === 1 // desktop: image on the right
  const slide = flip ? -60 : 60

  return (
    <div
      ref={rowRef}
      className="relative grid grid-cols-1 items-center gap-6 pl-14 md:grid-cols-[1fr_8rem_1fr] md:gap-0 md:pl-0"
    >
      {/* IMAGE – shown in full, never cropped */}
      <motion.figure
        initial={{ opacity: 0, y: 60, scale: 0.94 }}
        animate={active ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 60, scale: 0.94 }}
        transition={{ duration: 1, ease: EASE, delay: 0.1 }}
        className={`relative m-0 ${flip ? "md:order-3 md:pl-2" : "md:order-1 md:pr-2"}`}
      >
        <div className="relative">
          <Corner active={active} delay={0.5} className="-left-2 -top-2 border-l-2 border-t-2" />
          <Corner active={active} delay={0.6} className="-right-2 -top-2 border-r-2 border-t-2" />
          <Corner active={active} delay={0.7} className="-bottom-2 -left-2 border-b-2 border-l-2" />
          <Corner active={active} delay={0.8} className="-bottom-2 -right-2 border-b-2 border-r-2" />

          <div className="relative overflow-hidden bg-black shadow-[0_20px_60px_-20px_rgba(201,162,77,0.35)]">
            <motion.img
              src={data.img}
              alt={data.title}
              initial={{ scale: 1.18 }}
              animate={{ scale: active ? 1 : 1.18 }}
              transition={{ duration: 1.8, ease: EASE }}
              className="block h-auto w-full"
            />
            {/* gold curtain wipes away */}
            <motion.div
              initial={{ scaleX: 1 }}
              animate={{ scaleX: active ? 0 : 1 }}
              transition={{ duration: 1.1, ease: EASE, delay: 0.25 }}
              style={{ originX: flip ? 0 : 1 }}
              className="absolute inset-0 bg-[#C9A24D]"
            />
          </div>
        </div>
      </motion.figure>

      {/* spacer column where the road passes (desktop) */}
      <div className="hidden md:order-2 md:block" />

      {/* TEXT */}
      <div className={`relative ${flip ? "md:order-1 md:pr-6 md:text-right" : "md:order-3 md:pl-6"}`}>
        <motion.span
          initial={{ opacity: 0, scale: 0.6 }}
          animate={active ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.6 }}
          transition={{ type: "spring", stiffness: 240, damping: 16, delay: 0.3 }}
          className={`mb-4 inline-flex items-center gap-2 rounded-full border border-[#C9A24D]/50 bg-[#C9A24D]/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.25em] text-[#C9A24D] ${
            flip ? "md:flex-row-reverse" : ""
          }`}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-[#C9A24D]" />
          Stop {String(index + 1).padStart(2, "0")}
        </motion.span>

        <motion.p
          initial={{ opacity: 0, x: slide }}
          animate={active ? { opacity: 1, x: 0 } : { opacity: 0, x: slide }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.4 }}
          className="text-xs font-medium uppercase tracking-[0.35em] text-[#C9A24D] md:text-sm"
        >
          {data.period}
        </motion.p>

        <h3 className="mt-3 text-2xl font-bold leading-tight text-white md:text-3xl lg:text-4xl">
          <MaskedWords text={data.title} active={active} delay={0.5} />
        </h3>

        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: active ? 1 : 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.9 }}
          className={`my-5 h-[2px] w-16 bg-[#C9A24D] ${flip ? "origin-right md:ml-auto" : "origin-left"}`}
        />

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={active ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
          transition={{ duration: 0.9, ease: EASE, delay: 1 }}
          className="text-base leading-relaxed text-gray-400 md:text-lg"
        >
          {data.text}
        </motion.p>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Section                                                            */
/* ------------------------------------------------------------------ */
const JourneySection = () => {
  const trackRef = useRef(null)
  const rowRefs = useRef([])
  const [layout, setLayout] = useState({ w: 0, h: 0, stops: [], desktop: true })
  const [fracs, setFracs] = useState([])
  const [reached, setReached] = useState(0)
  const reduce = useReducedMotion()

  // Measure real positions so the road threads through every milestone
  useEffect(() => {
    const el = trackRef.current
    if (!el) return
    const measure = () => {
      const w = el.offsetWidth
      const h = el.offsetHeight
      const stops = rowRefs.current.map((r) => (r ? r.offsetTop + r.offsetHeight / 2 : 0))
      const desktop = window.matchMedia("(min-width: 768px)").matches
      setLayout((p) =>
        p.w === w && p.h === h && p.desktop === desktop && p.stops.join() === stops.join()
          ? p
          : { w, h, stops, desktop }
      )
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    rowRefs.current.forEach((r) => r && ro.observe(r))
    window.addEventListener("load", measure)
    return () => {
      ro.disconnect()
      window.removeEventListener("load", measure)
    }
  }, [])

  const { scrollYProgress } = useScroll({
    target: trackRef,
    offset: ["start 65%", "end 55%"],
  })
  const progress = useSpring(scrollYProgress, { stiffness: 70, damping: 22, mass: 0.5 })

  const handleStops = (next) =>
    setFracs((prev) =>
      prev.length === next.length && prev.every((v, i) => Math.abs(v - next[i]) < 0.001) ? prev : next
    )

  // A stop counts as "reached" once the car gets there (it never un-reaches)
  const update = (p) => {
    const n = fracs.filter((f) => p >= f - 0.005).length
    setReached((prev) => (n > prev ? n : prev))
  }
  useMotionValueEvent(progress, "change", update)
  useEffect(() => {
    update(progress.get())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fracs])

  return (
    <section className="overflow-hidden bg-[#0f0f0f] pb-28 pt-8 text-white">
      <div className="mx-auto max-w-7xl px-6">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mb-16 text-center md:mb-24"
        >
          <p className="mb-3 flex items-center justify-center gap-3 text-sm tracking-widest text-[#C9A24D]">
            <span className="h-px w-8 bg-[#C9A24D]" />
            THE ROADMAP
            <span className="h-px w-8 bg-[#C9A24D]" />
          </p>
          <h2 className="text-4xl font-bold md:text-6xl">Our Journey</h2>
        </motion.div>

        {/* Road + chapters */}
        <div ref={trackRef} className="relative flex flex-col gap-20 pb-24 md:gap-36 md:pb-32">
          <Road
            size={{ w: layout.w, h: layout.h }}
            stops={layout.stops}
            desktop={layout.desktop}
            progress={progress}
            reached={reduce ? chapters.length : reached}
            onStops={handleStops}
          />

          {chapters.map((c, i) => (
            <Chapter
              key={c.title}
              data={c}
              index={i}
              active={reduce || i < reached}
              rowRef={(el) => (rowRefs.current[i] = el)}
            />
          ))}
        </div>
      </div>
    </section>
  )
}

export default JourneySection
